import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export default function BenefitsSection() {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  const BENEFITS = [
    {
      icon: "ri-heart-pulse-fill",
      title: t('benefits.items.0.title'),
      desc: t('benefits.items.0.desc'),
      color: "#13B6EC",
    },
    {
      icon: "ri-book-open-fill",
      title: t('benefits.items.1.title'),
      desc: t('benefits.items.1.desc'),
      color: "#0E8FBD",
    },
  ];



  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const getRevealStyle = (delay: number = 0): React.CSSProperties => ({
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? "translateY(0)" : "translateY(24px)",
    transition: "opacity 0.6s ease-out, transform 0.6s ease-out",
    transitionDelay: `${delay}ms`,
  });

  return (
    <section ref={sectionRef} className="py-16 md:py-24 bg-gradient-to-b from-[#f7fbff] to-white">
      <div className="max-w-3xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="benefit-reveal text-center mb-10" style={getRevealStyle(0)}>
          <h2
            className="text-xl sm:text-2xl md:text-3xl font-black text-gray-900"
            style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
          >
            {t('benefits.title')}
          </h2>
        </div>

        {/* 2 Benefits */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {BENEFITS.map((benefit, idx) => (
            <div
              key={benefit.title}
              className="benefit-reveal flex items-start gap-4 p-5 rounded-2xl"
              style={{
                ...getRevealStyle((idx + 1) * 120),
                background: "rgba(255,255,255,0.8)",
                boxShadow: "0 2px 16px rgba(19,182,236,0.06)",
                border: "1px solid rgba(19,182,236,0.08)",
              }}
            >
              <div
                className="flex-shrink-0 w-11 h-11 flex items-center justify-center rounded-xl"
                style={{ background: `${benefit.color}12` }}
              >
                <i className={`${benefit.icon} text-xl`} style={{ color: benefit.color }} />
              </div>
              <div>
                <h3
                  className="text-base font-bold text-gray-900 mb-1"
                  style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                >
                  {benefit.title}
                </h3>
                <p
                  className="text-sm text-gray-500 leading-relaxed"
                  style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                >
                  {benefit.desc}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
